<template>
  <login-layout class="view-invite">
    <v-overlay :value="loading||formState.sending">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
    <div v-if="!loading">
      <div class="logout-button mt-5 mr-5" v-if="logged">
        <v-icon @click="navigate('logout')">logout</v-icon>
      </div>
      <div class="pa-6" v-if="invitation.status == 'accepted'">
        <v-row justify="space-around">
          <v-col cols="auto" class="text-center">
            <v-avatar color="green" size="185" class="ml-7 mb-3 mt-6">
              <v-icon dark size="100">assignment_turned_in</v-icon>
            </v-avatar>
            <p>You have already accepted this invitation.</p>
            <p><router-link to="/">Click here</router-link> to access this account.</p>
          </v-col>
        </v-row>
      </div>
      <div class="pa-6" v-else-if="invitation.status == 'declined'">
        <v-row justify="space-around">
          <v-col cols="auto" class="text-center">
            <v-avatar color="black" size="185" class="ml-7 mb-3 mt-6">
              <v-icon dark size="100">delete_forever</v-icon>
            </v-avatar>
            <p>You have already declined this invitation.</p>
            <p>Please contact {{invitation.inviter.first_name}} {{invitation.inviter.last_name}} if you need a new one.</p>
          </v-col>
        </v-row>
      </div>
      <div class="pa-6" v-else-if="invitation.status == 'cancelled'">
        <v-row justify="space-around">
          <v-col cols="auto" class="text-center">
            <v-avatar color="black" size="185" class="ml-7 mb-3 mt-6">
              <v-icon dark size="100">cancel</v-icon>
            </v-avatar>
            <p>This invitation has been cancelled by {{invitation.inviter.first_name}} {{invitation.inviter.last_name}}.</p>
            <p>Please contact {{invitation.inviter.first_name}} {{invitation.inviter.last_name}} if you need a new one.</p>
          </v-col>
        </v-row>
      </div>
      <div v-else class="h-50">
        <v-skeleton-loader v-if="logged==undefined"
          class="mx-auto"
          max-width="100%"
          type="card"
        ></v-skeleton-loader>
        <div v-else-if="logged==true" class="pa-6">
          <v-alert type="error" v-if="user.email.toLowerCase() != invitation.email.toLowerCase()" class="mt-3">
            <p>User email and invitation email do not match.</p>
            <p>You must log in under {{ invitation.email }}</p>
          </v-alert>
          <v-alert type="error" v-if="formState.error.length">
            {{ formState.error }}
          </v-alert>
          <div>
            <p>Hi {{ user.first_name }},</p>
            <p>{{invitation.inviter.first_name}} {{invitation.inviter.last_name}} invited you to their account.</p>

            <v-btn large
                   color="primary darken-4 white--text"
                   :disabled="formState.sending"
                   @click="accept">
              Accept
            </v-btn>

            <v-btn large
                   color="darken-4"
                   class="ml-4"
                   :disabled="formState.sending"
                   @click="decline">
              Decline
            </v-btn>
          </div>
        </div>
        <div v-else-if="logged==false" class="pa-3 pt-12 text-center">
            <h3 class="text-center">Hi!</h3>
            <div class="mb-3">
              <p>{{invitation.inviter.first_name}} {{invitation.inviter.last_name}} invited you to their account.</p>
              <p>To accept this invite, please create your login details...</p>
              <v-alert
                border="left"
                colored-border
                color="primary accent-4"
                elevation="1"
                class="pre-formatted"
                v-if="invitation.notes">{{ invitation.notes }}</v-alert>
            </div>

            <v-alert type="error" v-if="formState.error.length">
              {{ formState.error }}
            </v-alert>

            <div>
              <v-spacer></v-spacer>
              <div v-if="!formState.display">
                <v-btn
                  color="primary darken-4 white--text"
                  width="170"
                  @click="formState.display=!formState.display">Create account</v-btn>
                <div class="mt-3 mb-3"><strong>OR</strong> login with an existing account...</div>
                <v-btn
                  outlined
                  width="170"
                  to="/login">Login</v-btn>
              </div>

              <v-form
                v-if="formState.display"
                lazy-validation
                class="ma-3 mt-5">
                <v-text-field
                  v-model="form.email"
                  label="E-mail"
                  name="email"
                  prepend-icon="email"
                  type="text"
                  color="primary darken-4"
                  readonly
                  filled
                  disabled
                ></v-text-field>
                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="form.first_name"
                      label="First name"
                      name="first_name"
                      prepend-icon="person"
                      type="text"
                      color="primary darken-4"
                      :error="!$v.form.first_name.required&&formState.validation"
                      :error-messages="!$v.form.first_name.required&&formState.validation?'First name is required':''"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="form.last_name"
                      label="Last name"
                      name="last_name"
                      prepend-icon="person"
                      type="text"
                      color="primary darken-4"
                      :error="!$v.form.last_name.required&&formState.validation"
                      :error-messages="!$v.form.last_name.required&&formState.validation?'Last name is required':''"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-text-field
                  v-model="form.phone"
                  label="Phone"
                  name="phone"
                  prepend-icon="phone"
                  type="text"
                  color="primary darken-4"
                ></v-text-field>

                <v-row>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="form.password"
                      label="Password"
                      name="password"
                      prepend-icon="lock"
                      :append-icon="formState.passwordMode ? 'visibility_off' : 'visibility'"
                      @click:append="() => (formState.passwordMode = !formState.passwordMode)"
                      :type="formState.passwordMode ? 'password' : 'text'"
                      color="primary darken-4"
                      :error="formState.validation && $v.form.password.$error"
                      :error-messages="$v.form.password.$error ? passwordErrors($v) : ''"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="form.repeatPassword"
                      label="Repeat password"
                      name="repeatPassword"
                      prepend-icon="lock"
                      :append-icon="formState.passwordMode ? 'visibility_off' : 'visibility'"
                      @click:append="() => (formState.passwordMode = !formState.passwordMode)"
                      :type="formState.passwordMode ? 'password' : 'text'"
                      color="primary darken-4"
                      :error="formState.validation && $v.form.repeatPassword.$error"
                      :error-messages="$v.form.repeatPassword.$error ? passwordRepeatErrors($v) : ''"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <div class="position-relative">
                  <v-checkbox
                    v-model="form.agreed"
                    :error="!$v.form.agreed.sameAs&&formState.validation"
                    :error-messages="!$v.form.agreed.sameAs&&formState.validation?'You must agree to continue':''"
                    label="I accept"
                  />
                  <a href="/legal-tos" target="_blank" class="terms-link">terms and conditions</a>
                </div>

                <v-btn large
                       color="primary darken-4 white--text"
                       :disabled="formState.sending"
                       @click="register">
                  Create and accept
                </v-btn>

                <v-btn large
                       text
                       :disabled="formState.sending"
                       @click="formState.display=false">
                  Cancel
                </v-btn>
              </v-form>
            </div>
        </div>
      </div>
    </div>
    <v-row justify="center">
      <v-dialog v-model="error" persistent max-width="290">
        <v-card>
          <v-card-title class="headline">Error</v-card-title>
          <v-card-text>
            {{ errorMessage }}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="black" text @click="error = false">OK</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-row>
  </login-layout>
</template>

<script>
    import Invitation from "../models/invitation";
    import User from "../models/user";
    import { required, sameAs } from 'vuelidate/lib/validators'
    import { passwordValidators, passwordRepeatValidators, passwordErrorMessage, passwordRepeatErrorMessage } from '../helpers/passwordHelpers'

    export default {
        name: "Invite",
        data() {
            return {
                user: new User(),
                logged: undefined,
                invitation: {},
                error: false,
                errorMessage: '',
                formState: {
                    display: false,
                    sending: false,
                    validation: false,
                    passwordMode: true,
                    error: ''
                },
                form: {
                    agreed: false,
                    email: '',
                    first_name: '',
                    last_name: '',
                    phone: '',
                    password: '',
                    repeatPassword: '',
                },
            };
        },

        validations: {
            form: {
                first_name: { required },
                last_name: { required },
                agreed: {
                    sameAs: sameAs( () => true )
                },
                password: passwordValidators,
                repeatPassword: passwordRepeatValidators('form', 'password')
            }
        },

        mounted() {
            this.getData()
        },

        computed: {
            loading () {
                return this.$store.state.loading
            }
        },

        methods: {
            register() {
                this.formState.validation = true;
                this.$v.form.$touch();

                if (this.$v.$invalid) {
                    this.formState.sending = false;
                } else {
                    this.formState.sending = true;
                    this.$inviteService.remove();
                    this.formState.error = '';

                    this.$inviteService.register(this.form, this.$route.params.invite)
                        .then(() => {
                            this.$router.replace({ name: 'home' })
                        })
                        .catch((error) => {
                            this.formState.error = error.response.data.error;
                        }).finally(() => this.formState.sending = false);
                }
            },

            async accept() {
                this.formState.sending = true;
                this.formState.error = '';
                this.$inviteService.remove();

                await this.$inviteService.accept(this.$route.params.invite)
                    .then(response => {
                        if (typeof response != undefined) {
                            this.invitation = new Invitation(response.data);
                            setTimeout(()=>this.$router.replace({ name: 'home' }), 1500)
                        }
                    })
                    .catch((error) => {
                        this.formState.error = error.response.data.error;
                    }).finally(() => this.formState.sending = false);
            },

            async decline () {
                this.formState.sending = true;
                this.formState.error = '';
                this.$inviteService.remove();

                this.$inviteService.decline(this.$route.params.invite)
                    .then(response => {
                        if (typeof response != undefined) {
                            this.invitation = new Invitation(response.data);
                        }
                    })
                    .catch((error) => {
                        this.formState.error = error.response.data.error;
                    }).finally(() => this.formState.sending = false);
            },

            async getData() {
                this.$store.commit('loadingOn')

                try {
                    await Promise.all([
                        this.getUser(),
                        this.getInvite(),
                    ]);
                } catch (e) {
                    if (this.$isDevEnviroment) {
                        console.log(e);
                    } else {
                        await this.$router.replace({ name: 'error' })
                    }
                }

                this.$store.commit('loadingOff')
            },

            async getUser() {
                await this.$sessionService.isLogged()
                    .then(
                        () => {
                            this.$sessionService.current().then(
                                response => {
                                    this.user = new User(response.login)
                                    this.logged = true
                                    this.$inviteService.remove()
                                }).catch(() => Promise.resolve());
                        }
                    ).catch(() => {
                        this.logged = false
                        this.$inviteService.store(this.$route.params.invite);
                        Promise.resolve()
                    });
            },

            async getInvite() {
                await this.$inviteService.getInvite(this.$route.params.invite).then(
                    (response)=> {
                        this.invitation = new Invitation(response);
                        this.form.email = this.invitation.email;
                    }
                ).catch(
                    (error)=> {
                        this.error = true
                        this.errorMessage = error.response.data.error
                        setTimeout(()=>{this.$router.replace({ name: 'error' })}, 3000)
                    }
                );
            },

            navigate(route) {
                if (route) {
                    this.$router.replace({
                        name: route
                    });
                }
            },

            passwordErrors($v) {
                return passwordErrorMessage($v.form.password)
            },

            passwordRepeatErrors($v) {
                return passwordRepeatErrorMessage($v.form.repeatPassword)
            }
        }
    }
</script>

<style>
  .v-input--is-readonly .v-input__control .v-input__slot::before {
    border-color: transparent;
  }
  .logout-button {
    position: absolute;
    right: 0;
    top: 0;
  }
  .pre-formatted {
    white-space: pre;
  }
  .terms-link {
    position: absolute;
    top: 4px;
    left: 95px;
  }
  .position-relative {
    position: relative;
  }
  .view-invite .v-messages__message {
    white-space: pre;
  }
</style>